import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import {
  Box,
  Card,
  RatingIcons,
  Stack,
  Typography,
  CardContent,
  CardHeader,
} from '@packages/shared';
import { defineMessage, useIntl } from 'react-intl';
import { Response } from '../Response';
import { ReviewMenu } from '../ReviewMenu';
import { ReviewTags } from '../ReviewTags';

const ReviewFragment = graphql(/* GraphQL */ `
  fragment ReviewFragment on RatingReview {
    id
    created
    description
    rating
    reviewer {
      nickname
      location
    }
    reviewTags {
      ...ReviewTagsFragment
    }
    title
    responses {
      ...ResponseFragment
    }
  }
`);

const message = defineMessage({
  defaultMessage: 'von {authorNickName} | {created}',
  id: 'rating.author',
});

type ReviewProps = {
  /**
   * Review fragment data (__typename RatingReview)
   */
  reviewData: FragmentType<typeof ReviewFragment>;
  /**
   * Whether to show action menu
   * @default true
   */
  showMenu?: boolean;
};

export const Review = ({ reviewData, showMenu = true }: ReviewProps) => {
  const { id, reviewer, rating, created, title, description, responses, reviewTags } =
    getFragmentData(ReviewFragment, reviewData);
  const { formatMessage } = useIntl();

  return (
    <Card sx={{ width: '100%' }}>
      <CardHeader
        sx={{
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'grey.main',
        }}
        action={showMenu && <ReviewMenu id={id} />}
        title={
          <Stack spacing={0.5}>
            <RatingIcons avgRating={rating} size="small" reviewCount={0} />
            <Typography variant="body2" sx={{ color: 'text.darkTransparent' }}>
              {formatMessage(message, {
                authorNickName: reviewer.nickname,
                created,
              })}
            </Typography>
          </Stack>
        }
        disableTypography
      />
      <CardContent>
        <Stack spacing={1}>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {title}
            </Typography>
            <Typography variant="body2" whiteSpace="pre-wrap">
              {description}
            </Typography>
          </Box>
          {reviewTags && <ReviewTags reviewTagsData={reviewTags} />}
        </Stack>
        {responses?.map((response, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Response responseData={response} sx={{ mt: 1.675 }} key={i} />
        ))}
      </CardContent>
    </Card>
  );
};
